import { Injectable, inject } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import { cloneDeep, merge } from 'lodash';
import { colorMapping } from '../ag1-chart-color-palette';
import {
  HighChartsCustomConfig,
  SeriesData,
  RefactorDataDict,
  ReportConfig,
} from '@app/core/interfaces/report.interface';
import { FormatService } from './format.service';

export interface ReportData {
  name: string;
  data: any[];
}

@Injectable()
export class AG1GenericChartService {
  private formatService = inject(FormatService);

  chartOptions: Highcharts.Options = {};

  constructor() {}

  getOptions(
    reportConfig: ReportConfig,
    reportData: any[],
    defaultGeneralOptions: Highcharts.Options,
  ): Highcharts.Options {
    // Combine chart options
    this.chartOptions = this.combineChartOptions(
      reportConfig,
      defaultGeneralOptions,
      this.getDefaultChartOptions(),
    );

    // Prepare and extract xAxisCategories and seriesData
    const { xAxisCategories, seriesData } = this.extractXAxistCatsAndSeriesData(
      reportConfig,
      reportData,
    );

    // Update this.chartOptions with seriesData, and xAxisCategories
    this.updateWithXAxisCatsAndSeriesData(
      seriesData,
      xAxisCategories,
      reportConfig?.highChartsCustom?.timeSeries?.type
        ? (reportConfig?.highChartsCustom?.timeSeries as { type: string })
        : undefined,
    );

    // Update this.chartOptions with yAxis options based on highChartsCustom config and seriesData
    this.applyYAxisOptions(reportConfig?.highChartsCustom, seriesData);

    // Update this.chartOptions with series configurations like color mapping, type, and legend
    this.applySeriesConfig(reportConfig, reportData);

    // Update this.chartOptions to apply appropriate labels
    this.applyLabels();

    // Update this.chartOptions with data label formatter to compactly format numbers
    this.addDataLabelFormatter();

    return this.chartOptions;
  }
  /////////////////////////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  // #region               getOptions helpers                //
  /////////////////////////////////////////////////////////////
  private combineChartOptions(
    reportConfig: ReportConfig,
    defaultGeneralOptions: Highcharts.Options,
    defaultGenericChartOptions: Highcharts.Options,
  ): Highcharts.Options {
    const configChartOptions = cloneDeep(reportConfig?.chartOptions);

    // Combine default, generic, and config-specific options
    return merge(
      {},
      defaultGeneralOptions,
      defaultGenericChartOptions,
      configChartOptions,
    );
  }

  private getDefaultChartOptions(): Highcharts.Options {
    return {
      chart: { type: 'column' },
      legend: {
        // Position the legend to the right of the plots
        align: 'right',
        verticalAlign: 'top',
        layout: 'vertical',
        x: 0,
        y: 100,
        floating: false,
        itemStyle: {
          width: 150,
          textOverflow: 'ellipsis',
        },
        useHTML: true,
        maxHeight: 140, // Set max height to improve the look
        navigation: {
          enabled: true,
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderRadius: 5,
          minPointLength: 3, // Ensures small values are visible by setting a min height on columns
        },
      },
    };
  }

  private extractXAxistCatsAndSeriesData(
    reportConfig: ReportConfig,
    reportData: any[],
  ): { xAxisCategories: string[]; seriesData: SeriesData[] } {
    const highChartsCustomConfig = reportConfig?.highChartsCustom;
    const xAxis =
      (reportConfig?.chartOptions?.xAxis as Highcharts.XAxisOptions)?.id ||
      (reportConfig?.chartOptions?.xAxis as Highcharts.XAxisOptions)?.title
        ?.text;
    const seriesBreakout = highChartsCustomConfig?.seriesBreakout;

    // Determine series names based on report data and breakout configuration
    const seriesNames = this.getSeriesNames(reportData, xAxis, seriesBreakout);

    // Prepare data as time series or standard series based on configuration
    const filledReportData = highChartsCustomConfig?.timeSeries
      ? this.prepareTimeSeriesData(
          seriesNames,
          reportData,
          xAxis,
          seriesBreakout,
          highChartsCustomConfig.timeSeries.type,
        )
      : this.prepareStandardSeriesData(seriesNames, reportData);

    // Extract unique x-axis categories from filled data
    const xAxisCategories = this.getUniqueSeriesCategories(
      filledReportData[0].data,
      xAxis,
    );

    // Generate series data based on filled report data and categories
    const seriesData = this.generateSeriesData(
      filledReportData,
      xAxis,
      seriesBreakout,
      xAxisCategories,
      highChartsCustomConfig?.seriesYAxis,
    );

    return { xAxisCategories, seriesData };
  }

  private updateWithXAxisCatsAndSeriesData(
    seriesData: SeriesData[],
    xAxisCategories: any[],
    timeSeries?: { type: string },
  ) {
    this.chartOptions = merge({}, this.chartOptions, {
      series: seriesData,
      xAxis: {
        ...this.chartOptions.xAxis,
        categories: xAxisCategories,
      },
    });

    if (timeSeries) {
      this.chartOptions = merge(
        this.chartOptions,
        this.getTimeSeriesChartEventsOptions(),
      );
    }
  }

  private applyYAxisOptions(
    highChartsCustomConfig: HighChartsCustomConfig,
    seriesData: SeriesData[],
  ) {
    const timeSeriesYAxisOptions = [];

    // Set yAxis options for each axis
    (this.chartOptions.yAxis as Highcharts.YAxisOptions[]).forEach(
      (yAxis: Highcharts.YAxisOptions) => {
        yAxis.stackLabels = merge(
          {},
          { enabled: true, style: { color: '#1E293B', fontWeight: 'normal' } },
          yAxis.stackLabels,
          {
            formatter: ((component: AG1GenericChartService) =>
              function (this: Highcharts.AxisLabelsFormatterContextObject) {
                return component.formatService.formatNumberCompact(
                  (this as any).total,
                );
              })(this),
          },
        );

        // Set yAxis ID if not already defined
        yAxis.id = yAxis.id ? yAxis.id : yAxis.title.text;

        if (highChartsCustomConfig?.timeSeries) {
          const timeSeriesYAxisOption = this.getTimeSeriesYAxisOptions(
            yAxis,
            seriesData,
            highChartsCustomConfig.seriesBreakout,
          );
          timeSeriesYAxisOptions.push(merge({}, yAxis, timeSeriesYAxisOption));
        }
      },
    );

    // Override yAxis options with time series if applicable
    if (highChartsCustomConfig?.timeSeries) {
      this.chartOptions.yAxis = timeSeriesYAxisOptions;
    }
  }

  // Label visibility depends on whether chart is stacked or not
  // Stacked charts use stackLabels to display totals by default, so dataLabels are disabled
  // Grouped charts use dataLabels to display individual values by default
  private applyLabels() {
    const isStacked = this.chartOptions.plotOptions?.column?.stacking;

    this.chartOptions = merge(
      {},
      {
        plotOptions: {
          column: {
            dataLabels: {
              enabled: !isStacked ? true : false,
            },
          },
        },
      },
      this.chartOptions,
    );
  }

  private applySeriesConfig(reportConfig: ReportConfig, reportData: any[]) {
    const seriesTypeConfig: [key: string] =
      reportConfig?.highChartsCustom?.seriesType;
    const seriesColorConfig: [key: string] =
      reportConfig?.highChartsCustom?.seriesColor;
    const hasMultipleYAxes =
      (this.chartOptions?.yAxis as Highcharts.YAxisOptions[]).length > 1;

    this.chartOptions?.series.forEach(
      (series: Highcharts.SeriesColumnOptions) => {
        // apply series type based on config or default chart type
        series.type = seriesTypeConfig
          ? seriesTypeConfig[series.name]
          : this.chartOptions?.chart?.type;

        // apply colour mapping if not using multiple y-axes, and colour mapping is defined for the series
        if (!hasMultipleYAxes && colorMapping[series.name]) {
          series.color = colorMapping[series.name];
        }

        // apply custom colour mapping from report builder (overwrites any colour mapping like commodities)
        if (seriesColorConfig && seriesColorConfig[series.name]) {
          series.color = seriesColorConfig[series.name];
        }
      },
    );

    // Get xAxis and seriesBreakout configs
    const seriesBreakoutConfig = reportConfig?.highChartsCustom?.seriesBreakout;
    const xAxisConfig = reportConfig?.chartOptions
      .xAxis as Highcharts.XAxisOptions;
    const xAxisIdentifier = xAxisConfig?.id || xAxisConfig?.title?.text;

    // Determine series names based on report data and breakout configuration
    const seriesNames = this.getSeriesNames(
      reportData,
      xAxisIdentifier,
      seriesBreakoutConfig,
    );

    // disable legend if no seriesBreakout
    if (!hasMultipleYAxes && !seriesBreakoutConfig && seriesNames.length == 1) {
      this.chartOptions.legend = { enabled: false };
    }

    // Filter series by yAxis IDs
    const yAxisIDs = (
      this.chartOptions?.yAxis as Highcharts.YAxisOptions[]
    ).map((yAxis: Highcharts.YAxisOptions) => yAxis.id);
    this.chartOptions.series = this.chartOptions?.series.filter(
      (series: Highcharts.SeriesColumnOptions) =>
        yAxisIDs.includes(String(series.yAxis)),
    );
  }

  // Add data label formatter to compactly format numbers
  private addDataLabelFormatter() {
    this.chartOptions = merge({}, this.chartOptions, {
      plotOptions: {
        series: {
          dataLabels: {
            formatter: ((component: AG1GenericChartService) =>
              function (this: Highcharts.PointLabelObject) {
                // `this` refers to the data label context in Highcharts, so we access `y` directly
                return component.formatService.formatNumberCompact(this.y);
              })(this),
          },
        },
      },
    });
  }
  ///////////////////////////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  // #region           prepareChartData helpers              //
  /////////////////////////////////////////////////////////////
  // Determine which fields in the data should be plotted as individual series
  private getSeriesNames(
    reportData: any[],
    xAxis: string,
    seriesBreakout?: string,
  ): string[] {
    if (Array.isArray(reportData) && reportData.length > 0) {
      const firstItem = reportData[0];
      return Object.keys(firstItem).filter(
        (key) => key !== xAxis && key !== seriesBreakout,
      );
    }
    return [];
  }

  // Prepare time series data with filled data points
  private prepareTimeSeriesData(
    seriesNames: string[],
    reportData: any[],
    xAxis: string,
    seriesBreakout: string,
    timeSeriesType: string,
  ): ReportData[] {
    return seriesNames.map((seriesName) => ({
      name: seriesName,
      data: this.fillTimeSeriesData(
        cloneDeep(reportData),
        xAxis,
        seriesName,
        seriesBreakout,
        timeSeriesType,
      ),
    }));
  }

  // We alter the data so that all dates will be rendered (not just dates for which there is data).
  private fillTimeSeriesData(
    data: any[],
    xAxis: string,
    yAxis: string,
    seriesBreakout: string,
    timeSeriesType: string,
  ) {
    const seriesBreakoutValues = seriesBreakout
      ? this.getUniqueSeriesCategories(data, seriesBreakout)
      : [];

    const isoWeek = this.isISOWeek(data, xAxis);
    const [minDate, maxDate] = this.getDateRange(data, xAxis);
    const timeArray = this.generateTimeArray(
      minDate,
      maxDate,
      timeSeriesType,
      isoWeek,
    );

    // Format x-axis dates to match timeArray format
    this.formatXAxisDates(data, xAxis, timeSeriesType, isoWeek);

    // Generate contiguous data
    return this.generateContiguousData(
      data,
      timeArray,
      xAxis,
      yAxis,
      seriesBreakout,
      seriesBreakoutValues,
    );
  }

  // Get minimum and maximum dates in the data
  private getDateRange(data: any[], xAxis: string): [Date, Date] {
    const minDateRow = data.reduce((min, current) =>
      this.formatService.getJSDate(current[xAxis]) <
      this.formatService.getJSDate(min[xAxis])
        ? current
        : min,
    );
    const maxDateRow = data.reduce((max, current) =>
      this.formatService.getJSDate(current[xAxis]) >
      this.formatService.getJSDate(max[xAxis])
        ? current
        : max,
    );

    return [
      this.formatService.getJSDate(minDateRow[xAxis]),
      this.formatService.getJSDate(maxDateRow[xAxis]),
    ];
  }

  // Format x-axis dates in the data to match timeArray format
  private formatXAxisDates(
    data: any[],
    xAxis: string,
    timeSeriesType: string,
    isoWeek: boolean,
  ): void {
    data.forEach((row) => {
      row[xAxis] = this.formatService.getFormattedDate(
        this.formatService.getJSDate(row[xAxis]),
        timeSeriesType,
        isoWeek,
      );
    });
  }

  // Generate contiguous data based on timeArray and seriesBreakout values
  private generateContiguousData(
    data: any[],
    timeArray: any[],
    xAxis: string,
    yAxis: string,
    seriesBreakout: string,
    seriesBreakoutValues: string[],
  ): any[] {
    const contiguousData = [];

    timeArray.forEach((date) => {
      if (seriesBreakout) {
        seriesBreakoutValues.forEach((cat) => {
          const existingRow = data.find(
            (row) => row[xAxis] === date && row[seriesBreakout] === cat,
          );
          const yVal = existingRow ? existingRow[yAxis] : null;

          contiguousData.push({
            [xAxis]: date,
            [yAxis]: yVal,
            [seriesBreakout]: cat,
          });
        });
      } else {
        const existingRow = data.find((row) => row[xAxis] === date);
        const yVal = existingRow ? existingRow[yAxis] : null;

        contiguousData.push({
          [xAxis]: date,
          [yAxis]: yVal,
        });
      }
    });

    return contiguousData;
  }

  private generateTimeArray(
    startDate: Date,
    endDate: Date,
    granularity: string,
    iso_week?: boolean,
  ): string[] {
    const timeArray: string[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const formattedDate = this.formatService.getFormattedDate(
        new Date(currentDate),
        granularity,
        iso_week,
      );
      timeArray.push(formattedDate);

      switch (granularity) {
        case 'hourly':
          currentDate.setHours(currentDate.getHours() + 1);
          break;
        case 'daily':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'yearly':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
        default:
          throw new Error(
            `Unsupported time-series granularity: "${granularity}"`,
          );
      }
    }

    return timeArray;
  }

  // Prepare data for standard series (non-time series) without additional transformations
  private prepareStandardSeriesData(
    seriesNames: string[],
    reportData: any[],
  ): ReportData[] {
    return seriesNames.map((seriesName) => ({
      name: seriesName,
      data: reportData,
    }));
  }

  private getUniqueSeriesCategories(data: string[], key: string): string[] {
    const uniqueValues = new Set();
    data.forEach((row) => {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        const value = row[key] ?? 'Undefined';
        uniqueValues.add(value);
      }
    });
    return Array.from(uniqueValues) as string[];
  }

  private isISOWeek(data: any, xAxis: string): boolean {
    // ISO week regex pattern
    const ISO_WEEK_REGEX = /^\d{4}-W\d{2}$/;

    // Safely check: data array is not empty and the first element has a valid xAxis value
    // Then test for ISO week regex pattern
    const iso_week =
      data.length > 0 &&
      typeof data[0][xAxis] === 'string' &&
      ISO_WEEK_REGEX.test(data[0][xAxis]);

    return iso_week;
  }

  // Generate series data for chart without using flatMap
  private generateSeriesData(
    filledReportData: ReportData[],
    xAxis: string,
    seriesBreakout: string,
    xAxisCategories: any[],
    seriesYAxis: any,
  ): SeriesData[] {
    return filledReportData.reduce((acc: SeriesData[], series) => {
      const processedData = this.processReportData(
        series,
        xAxis,
        seriesBreakout,
        xAxisCategories,
        seriesYAxis,
      );
      return acc.concat(
        Array.isArray(processedData) ? processedData : [processedData],
      );
    }, []);
  }

  // Process report data and return a series data object
  private processReportData(
    reportData: ReportData,
    xAxis: string,
    seriesBreakout: string | undefined,
    xAxisCategories: string[],
    seriesYAxis?: [key: string],
  ): SeriesData | SeriesData[] {
    let result: SeriesData | SeriesData[];
    const name = reportData.name;
    const data = reportData.data;

    if (seriesBreakout) {
      const refactorDataDict: RefactorDataDict = data.reduce((dict, row) => {
        const seriesYValue = row[name];
        const breakoutValue = row[seriesBreakout] ?? 'Undefined';

        if (!dict[breakoutValue]) {
          dict[breakoutValue] = {
            x_axis_name: xAxis,
            values: new Array(xAxisCategories.length).fill(null),
          };
        }

        const xAxisValue = row[xAxis] ?? 'Undefined';
        const index = xAxisCategories.indexOf(xAxisValue);
        if (index !== -1) {
          dict[breakoutValue].values[index] = seriesYValue;
        }

        return dict;
      }, {} as RefactorDataDict);

      result = Object.entries(refactorDataDict).map(([key, value]) => ({
        name: key,
        data: value.values,
        yAxis: seriesYAxis
          ? seriesYAxis[name]
            ? seriesYAxis[name]
            : name
          : name,
      }));
    } else {
      const seriesData = data.map((row) => row[name]);
      result = {
        name: name,
        data: seriesData,
        yAxis: seriesYAxis
          ? seriesYAxis[name]
            ? seriesYAxis[name]
            : name
          : name,
      };
    }

    return result;
  }
  ///////////////////////////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  // #region           applyChartOptions helpers              //
  /////////////////////////////////////////////////////////////
  private getTimeSeriesChartEventsOptions() {
    return {
      chart: {
        events: {
          load: function () {
            const uniqueXValuesCount = this.series[0].data.length; // Total unique x values
            let totalLabelWidth = 0;
            let labelCount = 0;

            // Calculate average label width based on existing tick labels
            const tickPositions = this.xAxis[0].tickPositions || [];
            tickPositions.forEach((position) => {
              const tick = this.xAxis[0].ticks[position];
              if (tick && tick.label && tick.label.getBBox) {
                totalLabelWidth += tick.label.getBBox().width;
                labelCount++;
              }
            });

            const avgLabelWidth =
              labelCount > 0 ? totalLabelWidth / labelCount : 0;

            // Calculate total required width if all unique x values had labels
            const estimatedTotalWidth = uniqueXValuesCount * avgLabelWidth;

            // tickLabelPercentage represents the percentage of the total x-axis width taken by a tick label divided by 2
            // this value is required to dynamically adjust the x-axis offset (ensuring that the first x-axis category label is not obscured)
            const tickLabelPercentage =
              0.5 * (avgLabelWidth / estimatedTotalWidth) * 100;

            // Check if we need a scrollbar by comparing estimated width with chart width
            if (estimatedTotalWidth > this.chartWidth) {
              this.update({
                chart: {
                  scrollablePlotArea: {
                    minWidth: Math.max(1400, estimatedTotalWidth), // Set minimum width based on estimated width
                    opacity: 0.9,
                    scrollPositionX: 0,
                  },
                },
                xAxis: {
                  left: `${tickLabelPercentage}%`, // Set left margin based on half tick label width as a percentage of the total x-axis width
                  width: `${100 - tickLabelPercentage}%`, // Adjust width accordingly
                  labels: { step: 1 },
                },
              });
            }
          },
        },
      },
    };
  }
  ///////////////////////////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  // #region           applyYAxisOptions helpers              //
  /////////////////////////////////////////////////////////////
  getTimeSeriesYAxisOptions(
    yAxis: Highcharts.YAxisOptions,
    seriesData: SeriesData[],
    seriesBreakout?: string,
  ) {
    const seriesRows = seriesData.filter((row) => row.yAxis === yAxis.id);
    if (seriesRows.length == 0) {
      return {};
    }

    let maxValue = 0;
    if (seriesBreakout) {
      let maxPositionalSum = 0;
      const seriesLength = seriesRows[0].data.length;
      for (let i = 0; i < seriesLength; i++) {
        const positionalSum = seriesRows.reduce(
          (sum, row) => sum + row.data[i],
          0,
        );
        if (positionalSum > maxPositionalSum) {
          maxPositionalSum = positionalSum;
        }
      }
      maxValue = maxPositionalSum;
    } else {
      seriesRows.forEach((row) => {
        const maxInRow = Math.max(...row.data);
        if (maxInRow > maxValue) {
          maxValue = maxInRow;
        }
      });
    }

    return { max: maxValue };
  }
}
